import { z } from 'zod';
import { CHANNELS, INSTALLMENT_STATUSES, SEQUENCE_STATUSES, USER_ROLES } from '../domain';
import { isoDateSchema } from './referentials';

// Vues des écrans du portail (BA-022, BA-025, BA-026, spec 08).

// --- Échéances ---

export const installmentQuerySchema = z.object({
  status: z.enum(INSTALLMENT_STATUSES).optional(),
  dueFrom: isoDateSchema.optional(),
  dueTo: isoDateSchema.optional(),
  minAmount: z.coerce.number().int().min(0).optional(),
  maxAmount: z.coerce.number().int().min(0).optional(),
  policyNumber: z.string().trim().max(64).optional(),
  insurerId: z.string().optional(),
  productLineId: z.string().optional(),
  agentId: z.string().optional(),
  preferredChannel: z.enum(CHANNELS).optional(),
  /** Séquence en pause, ou escaladée. */
  sequenceStatus: z.enum(SEQUENCE_STATUSES).optional(),
  /** Assurés sans numéro joignable. */
  noPhone: z.enum(['true', 'false']).optional(),
  /** Score de risque d'impayé minimal (BA-078), 60 pour « risque élevé ». */
  minRisk: z.coerce.number().int().min(0).max(100).optional(),
  cursor: z.string().max(64).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export const installmentRowSchema = z.object({
  id: z.string(),
  dueDate: z.string(),
  amountDue: z.number().int(),
  amountPaid: z.number().int(),
  status: z.enum(INSTALLMENT_STATUSES),
  contract: z.object({
    id: z.string(),
    policyNumber: z.string(),
    productLabel: z.string().nullable(),
    insurerId: z.string(),
    agentName: z.string().nullable(),
  }),
  policyholder: z.object({
    id: z.string(),
    displayName: z.string(),
    preferredChannel: z.enum(CHANNELS),
  }),
  sequence: z
    .object({
      id: z.string(),
      status: z.enum(SEQUENCE_STATUSES),
      currentStepNo: z.number().int(),
      nextRunAt: z.string().nullable(),
    })
    .nullable(),
  risk: z.object({ score: z.number().int(), factors: z.array(z.string()) }).nullable(),
});

export const installmentPageSchema = z.object({
  items: z.array(installmentRowSchema),
  nextCursor: z.string().nullable(),
});

export const timelineEntrySchema = z.object({
  at: z.string(),
  kind: z.enum(['sequence', 'message', 'payment', 'audit']),
  label: z.string(),
  detail: z.record(z.string(), z.unknown()),
});

export const installmentDetailSchema = installmentRowSchema.extend({
  periodStart: z.string(),
  periodEnd: z.string(),
  graceUntil: z.string().nullable(),
  suspensionAt: z.string().nullable(),
  source: z.enum(['computed', 'imported', 'api']),
  timeline: z.array(timelineEntrySchema),
});

// --- Tableau de bord ---

export const dashboardQuerySchema = z.object({
  insurerId: z.string().optional(),
  productLineId: z.string().optional(),
  agentId: z.string().optional(),
});

export const dashboardSchema = z.object({
  today: z.string(),
  draftMode: z.boolean(),
  tiles: z.object({
    /** Reste à encaisser sur les échéances des 30 prochains jours (millimes). */
    dueNext30: z.number().int(),
    /** Encaissé sur 30 jours glissants (millimes). */
    recovered30: z.number().int(),
    /** Part des échéances des 90 derniers jours payées au plus tard à leur date. */
    paidBeforeDueRate: z.number().nullable(),
    /** Reste dû sur les impayés de plus de 15 jours (millimes). */
    overdueOver15: z.number().int(),
    activeSequences: z.number().int(),
    messagesThisMonth: z.object({ count: z.number().int(), costMillimes: z.number().int() }),
  }),
  dueToday: z.array(
    z.object({
      id: z.string(),
      policyNumber: z.string(),
      amountDue: z.number().int(),
      status: z.string(),
    }),
  ),
  openEscalations: z.array(
    z.object({
      id: z.string(),
      reason: z.string(),
      openedAt: z.string(),
      installmentId: z.string().nullable(),
    }),
  ),
  lastPayments: z.array(
    z.object({
      id: z.string(),
      amount: z.number().int(),
      method: z.string(),
      confirmedAt: z.string().nullable(),
    }),
  ),
  alerts: z.array(
    z.object({ kind: z.string(), label: z.string(), count: z.number().int().optional() }),
  ),
  /** Dû et récupéré par semaine, 12 semaines jusqu'à la semaine en cours (BA-044). */
  weekly: z.array(
    z.object({ weekStart: z.string(), due: z.number().int(), recovered: z.number().int() }),
  ),
});

// --- Utilisateurs ---

export const userSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  role: z.enum(USER_ROLES),
  status: z.enum(['invited', 'active', 'locked', 'disabled']),
  totpEnabled: z.boolean(),
  lastLoginAt: z.string().nullable(),
});
export const userListSchema = z.object({ items: z.array(userSchema) });
export const userPatchSchema = z
  .object({
    role: z.enum(USER_ROLES),
    status: z.enum(['active', 'disabled']),
    name: z.string().trim().min(1).max(120),
  })
  .partial()
  .strict();

/** Tableau de bord de pilotage du cabinet (BA-093), sur 12 mois glissants. */
export const pilotageSchema = z.object({
  today: z.string(),
  periodFrom: z.string(),
  /** Portefeuille à échéance : reste à encaisser sous 30, 60 et 90 jours (cumulé). */
  dueWindows: z.array(
    z.object({ days: z.number().int(), count: z.number().int(), amount: z.number().int() }),
  ),
  /** Répartition sous 90 jours par compagnie. */
  dueByInsurer: z.array(
    z.object({
      insurerId: z.string(),
      name: z.string(),
      count: z.number().int(),
      amount: z.number().int(),
    }),
  ),
  renewal: z.object({
    renewed: z.number().int(),
    notRenewed: z.number().int(),
    rate: z.number().nullable(),
  }),
  /** Primes encaissées (millimes) : mois et année en cours. */
  collected: z.object({ month: z.number().int(), year: z.number().int() }),
  lost: z.object({
    count: z.number().int(),
    byStatus: z.array(
      z.object({ status: z.enum(['cancelled', 'lapsed', 'expired']), count: z.number().int() }),
    ),
    recent: z.array(
      z.object({
        contractId: z.string(),
        policyNumber: z.string(),
        policyholderName: z.string(),
        status: z.enum(['cancelled', 'lapsed', 'expired']),
        reason: z.string().nullable(),
        at: z.string(),
      }),
    ),
  }),
});
